// src/pages/patient/PatientAppointmentDetail.jsx
//
// /patient/appointments/:appointmentId -- the one appointment detail page
// (Patient Appointments Task 3).
//
//   * everything shown and every button offered comes from the server
//     (`allowed_actions`, payment block, pinned policy); the page never guesses;
//   * separate loaders for the detail, forms, payment, cancel, reschedule and
//     video, so one slow part never blanks another;
//   * a background refresh keeps data on screen and never overwrites form
//     input the patient is editing;
//   * after the Stripe return the payment params are removed from the URL and
//     the status is refreshed honestly (paid / cancelled / still confirming);
//   * the "booked" banner from the booking flow is shown once and not replayed
//     on a reload.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { readApiError } from "../../lib/apiErrors";
import { browserTimezone, formatDateTimeInZone, formatTimeInZone } from "../../lib/displayTimezone";
import useSequencedRequest from "../../lib/useSequencedRequest";
import { appointmentWorkspaceApi } from "../../services/patientAppointmentWorkspace";
import { describeCancellationPolicy } from "../../lib/cancellationPolicy";
import { InlineError, Pill, TimezoneNotice, classNames, modeLabel, modeTone, money } from "../../components/patient/booking/bookingUi";
import { STATUS_TONES } from "./PatientAppointments";
import AppointmentForms from "../../components/patient/appointments/AppointmentForms";
import CancelAppointmentDialog from "../../components/patient/appointments/CancelAppointmentDialog";
import PatientVideoPanel from "../../components/patient/appointments/PatientVideoPanel";
import RescheduleDialog from "../../components/patient/appointments/RescheduleDialog";
import InvoiceView, { InvoicePdfButton, raiseInvoiceIssueHref } from "../../components/patient/billing/InvoiceView";
import PaymentStatusCard from "../../components/patient/billing/PaymentStatusCard";

export const DETAIL_REFRESH_MS = 60000;
export const PAYMENT_POLL_MS = 3000;
export const PAYMENT_POLL_ATTEMPTS = 5;

const PAYMENT_PARAMS = ["payment", "session_id", "checkout", "stripe_session_id"];

const CANCELLED_STATUSES = ["cancelled_by_patient", "cancelled_by_clinic", "cancelled_by_doctor", "refunded"];
const FAILED_PAYMENT_STATES = ["failed", "canceled", "cancelled", "expired"];

// Authoritative payment outcome from the server's status read. Provider return
// parameters are never evidence of payment.
export function paymentOutcome(status) {
  if (!status) return "confirming";
  if (status.payment_status === "paid") return "paid";
  if (CANCELLED_STATUSES.includes(status.status)) return "ended";
  if (status.payment_status === "failed" || FAILED_PAYMENT_STATES.includes(String(status.latest_payment_status || "").toLowerCase())) return "failed";
  return "confirming";
}

// Online Pay / Retry is offered only when BOTH the lifecycle and the payment
// ledger allow it (a clinic may already have recorded the payment offline).
export function canPayOnline(data) {
  const actions = data?.allowed_actions || {};
  const ledger = data?.billing_payment;
  return Boolean(actions.can_retry_payment && (!ledger || ledger.allowed_actions?.can_retry_online_payment));
}

function ledgerSettled(data) {
  const status = data?.billing_payment?.payment_status;
  return Boolean(status && status !== "not_paid");
}

// The single most useful next step, taken only from server-provided state.
export function nextAction(data) {
  const actions = data.allowed_actions || {};
  const payment = data.payment || {};
  const pendingForms = data.forms_summary?.pending_required || 0;
  if (!ledgerSettled(data) && (canPayOnline(data) || payment.reserved_payment_required)) {
    return { tone: "warn", title: "Complete payment to keep this time", target: "payment" };
  }
  if (actions.can_join_video) return { tone: "violet", title: "Your video consultation is open — join now", target: "video" };
  if (pendingForms > 0) {
    return { tone: "info", title: `Complete ${pendingForms} required form${pendingForms === 1 ? "" : "s"} before your visit`, target: "forms" };
  }
  if (actions.is_terminal) return { tone: "neutral", title: "No further action is needed for this appointment.", target: null };
  return { tone: "success", title: "You're all set. We'll see you at your appointment.", target: null };
}

const TONE_BOX = {
  warn: "border-amber-300 bg-amber-50 text-amber-900",
  violet: "border-violet-200 bg-violet-50 text-violet-900",
  info: "border-sky-200 bg-sky-50 text-sky-900",
  success: "border-emerald-200 bg-emerald-50 text-emerald-900",
  neutral: "border-border bg-slate-50 text-slate-700",
};

function Row({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-border py-2.5 last:border-0 sm:flex-row sm:justify-between sm:gap-4">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</dt>
      <dd className="text-sm text-text sm:text-right">{children}</dd>
    </div>
  );
}

function Card({ title, id, children, action = null }) {
  return (
    <section id={id} aria-labelledby={`${id}-title`} className="scroll-mt-24 rounded-3xl border border-border bg-white p-5 shadow-soft">
      <div className="flex items-center justify-between gap-3">
        <h2 id={`${id}-title`} className="text-base font-semibold text-text">{title}</h2>
        {action}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function SummaryTile({ label, children, sub }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</div>
      <div className="mt-1 text-sm font-semibold text-text">{children}</div>
      {sub ? <div className="mt-0.5 text-xs text-muted">{sub}</div> : null}
    </div>
  );
}

export default function PatientAppointmentDetail() {
  const { appointmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [timeZone] = useState(() => browserTimezone() || "UTC");

  const [banner, setBanner] = useState(() => (location.state?.appointmentBooked ? "Appointment booked successfully." : ""));
  const [paymentNotice, setPaymentNotice] = useState(null); // {tone, text}
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  // The list's filters/page, carried in router state so Back restores them.
  const [listSearch] = useState(() => location.state?.listSearch || "");

  const detail = useSequencedRequest(null);
  const forms = useSequencedRequest(null);
  const dirtyForms = useRef(new Set());
  const paymentInFlight = useRef(false);
  const runDetail = detail.run;
  const runForms = forms.run;

  const loadDetail = useCallback(
    () => runDetail(({ signal }) => appointmentWorkspaceApi.detail(appointmentId, { signal })),
    [runDetail, appointmentId]
  );
  const loadForms = useCallback(
    ({ force = false } = {}) => {
      if (!force && dirtyForms.current.size) return; // never clobber unsaved input
      runForms(({ signal }) => appointmentWorkspaceApi.forms(appointmentId, { signal }));
    },
    [runForms, appointmentId]
  );

  useEffect(() => {
    loadDetail();
    loadForms({ force: true });
  }, [loadDetail, loadForms]);

  useEffect(() => {
    const id = window.setInterval(() => {
      loadDetail();
      loadForms();
    }, DETAIL_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [loadDetail, loadForms]);

  // The booked banner came through router state: show it, then drop the state
  // so a reload cannot claim success again.
  useEffect(() => {
    if (location.state?.appointmentBooked) navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate]);

  // Stripe return: strip payment params immediately, then confirm with the
  // server. The params are read once on mount; removing them from the URL must
  // not stop the confirmation poll.
  const [initialPaymentReturn] = useState(() =>
    PAYMENT_PARAMS.some((key) => searchParams.has(key)) ? searchParams.get("payment") || "unknown" : null
  );
  useEffect(() => {
    if (!initialPaymentReturn) return undefined;
    const params = new URLSearchParams(searchParams);
    PAYMENT_PARAMS.forEach((key) => params.delete(key));
    setSearchParams(params, { replace: true });
    if (initialPaymentReturn === "cancelled") {
      setPaymentNotice({ tone: "warn", text: "Payment was cancelled. Your appointment is still reserved until the payment deadline." });
      return undefined;
    }
    if (initialPaymentReturn !== "success") return undefined;

    let attempts = 0;
    let timer = null;
    let stopped = false;
    setPaymentNotice({ tone: "info", text: "Confirming your payment…" });
    const poll = async () => {
      attempts += 1;
      try {
        const status = await appointmentWorkspaceApi.paymentStatus(appointmentId);
        if (stopped) return;
        const outcome = paymentOutcome(status);
        if (outcome === "paid") {
          setPaymentNotice({ tone: "success", text: "Payment received. Your appointment is booked." });
          loadDetail();
          return;
        }
        if (outcome === "failed") {
          setPaymentNotice({
            tone: "warn",
            text: status.allowed_actions?.can_retry_payment
              ? "Your payment was not completed. The appointment is still reserved until the payment deadline — you can retry payment."
              : "Your payment was not completed.",
          });
          loadDetail();
          return;
        }
        if (outcome === "ended") {
          setPaymentNotice({ tone: "warn", text: "This reservation has ended and is no longer booked. No payment was confirmed." });
          loadDetail();
          return;
        }
      } catch {
        // retried below; a failed status read never claims success
      }
      if (stopped) return;
      if (attempts >= PAYMENT_POLL_ATTEMPTS) {
        setPaymentNotice({ tone: "warn", text: "We haven't received confirmation from the payment provider yet. This can take a few minutes — refresh this page shortly." });
        loadDetail();
        return;
      }
      timer = window.setTimeout(poll, PAYMENT_POLL_MS);
    };
    poll();
    return () => {
      stopped = true;
      if (timer) window.clearTimeout(timer);
    };
    // Runs once per Stripe return (mount only).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPaymentReturn]);

  const onDirtyChange = useCallback((id, dirty) => {
    if (dirty) dirtyForms.current.add(id);
    else dirtyForms.current.delete(id);
  }, []);

  async function retryPayment() {
    if (paymentInFlight.current) return;
    paymentInFlight.current = true;
    setPaymentBusy(true);
    setPaymentError("");
    try {
      const checkout = await appointmentWorkspaceApi.startCheckout(appointmentId);
      if (!checkout?.checkout_url) throw new Error("missing checkout url");
      window.location.assign(checkout.checkout_url);
    } catch (err) {
      setPaymentError(readApiError(err, "Payment could not be started. Your appointment is still reserved."));
      paymentInFlight.current = false;
      setPaymentBusy(false);
    }
  }

  const data = detail.data && detail.data.id === appointmentId ? detail.data : null;
  const backTo = listSearch ? `/patient/appointments?${listSearch}` : "/patient/appointments";

  if (!data) {
    if (detail.error) {
      const notFound = detail.error?.response?.status === 404;
      return (
        <div className="mx-auto max-w-3xl py-10">
          {notFound ? (
            <div className="rounded-3xl border border-border bg-white p-8 text-center shadow-soft" data-testid="appointment-not-found">
              <h1 className="text-lg font-semibold">Appointment not found</h1>
              <p className="mt-2 text-sm text-muted">It may have been removed, or the link is incorrect.</p>
              <Link to={backTo} className="mt-4 inline-flex rounded-2xl border border-border px-4 py-2 text-sm font-semibold">Back to appointments</Link>
            </div>
          ) : (
            <InlineError message={readApiError(detail.error, "The appointment could not be loaded.")} onRetry={loadDetail} />
          )}
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-6xl py-4" aria-busy="true" data-testid="detail-loading">
        <p className="sr-only" aria-live="polite">Loading appointment…</p>
        <div className="h-40 animate-pulse rounded-3xl border border-border bg-white" />
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((n) => <div key={n} className="h-20 animate-pulse rounded-2xl border border-border bg-white" />)}
        </div>
      </div>
    );
  }

  const actions = data.allowed_actions || {};
  const reasons = actions.unavailable_reasons || {};
  const payment = data.payment || {};
  const billingPayment = data.billing_payment || null;
  const isVideo = data.consultation_mode === "video";
  const next = nextAction(data);
  const sourceZone = data.doctor_timezone || data.timezone || null;
  const policy = describeCancellationPolicy(data.cancellation_policy, { currency: data.currency, startAt: data.start_at, timeZone });
  const preview = data.cancellation_preview || {};
  const pendingForms = data.forms_summary?.pending_required || 0;
  const totalForms = data.forms_summary?.total_patient_forms || 0;

  function afterCancel(result) {
    setCancelOpen(false);
    setActionMessage(result?.financial?.message && result.financial.financial_follow_up !== "none" ? `Appointment cancelled. ${result.financial.message}` : result?.message || "Appointment cancelled.");
    loadDetail();
  }

  function afterReschedule(result) {
    setRescheduleOpen(false);
    setActionMessage(result?.changed ? `Appointment moved to ${formatDateTimeInZone(result.appointment.start_at, timeZone)}.` : result?.message || "Nothing changed.");
    loadDetail();
    loadForms();
  }

  return (
    <div className="mx-auto w-full max-w-6xl py-2 sm:py-4">
      <Link to={backTo} className="text-sm font-semibold text-primary hover:underline">← All appointments</Link>

      {banner ? (
        <div role="status" className="mt-3 flex items-start justify-between gap-3 rounded-2xl border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="booked-banner">
          <span>{payment.reserved_payment_required ? "Appointment reserved — payment required" : banner}</span>
          <button type="button" onClick={() => setBanner("")} aria-label="Dismiss" className="text-emerald-800">×</button>
        </div>
      ) : null}
      {paymentNotice ? (
        <div role="status" data-testid="payment-notice" className={`mt-3 rounded-2xl border p-4 text-sm ${paymentNotice.tone === "success" ? "border-emerald-300 bg-emerald-50 text-emerald-800" : paymentNotice.tone === "warn" ? "border-amber-300 bg-amber-50 text-amber-900" : "border-sky-200 bg-sky-50 text-sky-800"}`}>
          {paymentNotice.text}
        </div>
      ) : null}
      {actionMessage ? <div role="status" className="mt-3 rounded-2xl border border-border bg-slate-50 p-4 text-sm">{actionMessage}</div> : null}
      {detail.error ? <div className="mt-3"><InlineError message={readApiError(detail.error, "Could not refresh this appointment.")} onRetry={loadDetail} /></div> : null}

      <header className="mt-4 rounded-3xl border border-border bg-white p-5 shadow-soft">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-text">{data.service_name}</h1>
            <p className="mt-1 text-sm text-muted">
              {data.doctor_name}
              {data.clinic_name ? <> · {data.clinic_name}</> : null}
              {" · "}
              <span>{data.appointment_number}</span>
            </p>
            <TimezoneNotice timeZone={timeZone} className="mt-1" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Pill tone={STATUS_TONES[data.status] || "info"}>{data.status_label}</Pill>
            <Pill tone={modeTone(data.consultation_mode)}>{modeLabel(data.consultation_mode) || data.consultation_mode}</Pill>
            {totalForms ? (
              <a href="#forms" className="focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
                <Pill tone={pendingForms ? "warn" : "success"}>{pendingForms ? "Forms pending" : "Forms complete"}</Pill>
              </a>
            ) : (
              <Pill tone="neutral">No forms</Pill>
            )}
            {detail.loading ? <span className="text-xs text-muted" aria-live="polite">Refreshing…</span> : null}
          </div>
        </div>

        <div className={classNames("mt-4 flex flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center sm:justify-between", TONE_BOX[next.tone])} data-testid="next-action">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide opacity-80">Next step</div>
            <div className="mt-0.5 text-sm font-semibold">{next.title}</div>
          </div>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Appointment actions">
            {canPayOnline(data) ? (
              <button type="button" onClick={retryPayment} disabled={paymentBusy} aria-busy={paymentBusy || undefined} className="rounded-2xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {paymentBusy ? "Opening payment…" : "Retry payment"}
              </button>
            ) : null}
            {actions.can_reschedule ? (
              <button type="button" onClick={() => setRescheduleOpen(true)} className="rounded-2xl border border-border bg-white px-4 py-2 text-sm font-semibold text-text">Reschedule</button>
            ) : null}
            {actions.can_cancel ? (
              <button type="button" onClick={() => setCancelOpen(true)} className="rounded-2xl border border-rose-300 bg-white px-4 py-2 text-sm font-semibold text-rose-700">Cancel appointment</button>
            ) : null}
          </div>
        </div>
        {!actions.can_reschedule && reasons.reschedule && !actions.is_terminal ? (
          <p className="mt-2 text-xs text-muted" data-testid="reschedule-unavailable">{reasons.reschedule.message}</p>
        ) : null}
        {paymentError ? <p role="alert" className="mt-2 text-sm text-rose-700">{paymentError}</p> : null}
      </header>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="summary-tiles">
        <SummaryTile label="Date & time" sub={timeZone}>
          {formatDateTimeInZone(data.start_at, timeZone)} – {formatTimeInZone(data.end_at, timeZone)}
        </SummaryTile>
        <SummaryTile label="Doctor" sub={data.clinic_name}>{data.doctor_name}</SummaryTile>
        <SummaryTile label="Mode" sub={data.location_name || (isVideo ? "Online" : null)}>{modeLabel(data.consultation_mode) || data.consultation_mode}</SummaryTile>
        <SummaryTile label="Amount" sub={data.currency}>{money(data.price, data.currency)}</SummaryTile>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card title="Appointment details" id="overview">
            <dl>
              <Row label="When">{formatDateTimeInZone(data.start_at, timeZone)} – {formatTimeInZone(data.end_at, timeZone)}</Row>
              <Row label="Duration">{data.duration_minutes} minutes</Row>
              <Row label="Your timezone">{timeZone}</Row>
              {sourceZone && sourceZone !== timeZone ? (
                <Row label="Appointment timezone">{formatDateTimeInZone(data.start_at, sourceZone)} ({sourceZone})</Row>
              ) : null}
              <Row label="Service">{data.service_name}{data.service_version_no ? ` (version ${data.service_version_no})` : ""}</Row>
              <Row label="Clinic">{data.clinic_name || "—"}</Row>
              <Row label="Location">{data.location_name || (isVideo ? "Online video" : "—")}</Row>
              {data.booked_for_patient_name ? <Row label="Booked for">{data.booked_for_patient_name}</Row> : null}
              <Row label="Your booking note">{data.patient_booking_note || "—"}</Row>
            </dl>
          </Card>

          {isVideo ? (
            <Card title="Video consultation" id="video">
              <PatientVideoPanel appointmentId={data.id} api={appointmentWorkspaceApi} canJoin={Boolean(actions.can_join_video)} unavailableMessage={reasons.join_video?.message} />
            </Card>
          ) : null}

          <Card title={`Forms (${totalForms})`} id="forms">
            <AppointmentForms appointmentId={data.id} data={forms.data} loading={forms.loading} error={forms.error}
              onRetry={() => loadForms({ force: true })} onSaved={() => { loadForms({ force: true }); loadDetail(); }} onDirtyChange={onDirtyChange} />
          </Card>

          {/* Billing: the ledger-derived payment state and the canonical invoice exactly as the
              server issued them -- read-only; no estimate and no payment-recording control. */}
          <Card title="Billing" id="billing" action={
            <button type="button" onClick={loadDetail} disabled={detail.loading} aria-busy={detail.loading || undefined}
              className="rounded-2xl border border-border bg-white px-3 py-1.5 text-xs font-semibold text-text hover:bg-slate-50 disabled:opacity-60" data-testid="billing-refresh">
              {detail.loading ? "Refreshing billing…" : "Refresh billing"}
            </button>
          }>
            {billingPayment ? (
              <div className="mb-4">
                <PaymentStatusCard payment={billingPayment} timeZone={timeZone} onRetry={canPayOnline(data) ? retryPayment : null}
                  retryBusy={paymentBusy} retryLabel="Retry payment" />
              </div>
            ) : null}
            {data.invoice ? (
              <div>
                <InvoiceView invoice={data.invoice} timeZone={timeZone} showAppointment={false} />
                <div className="mt-4 flex flex-wrap items-start gap-2">
                  <InvoicePdfButton invoice={data.invoice} download={appointmentWorkspaceApi.downloadInvoicePdf}
                    label={billingPayment?.allowed_actions?.can_download_receipt ? "Download receipt (PDF)" : "Download invoice (PDF)"} />
                  <Link to={raiseInvoiceIssueHref(data.invoice)} className="rounded-2xl border border-border bg-white px-4 py-2 text-sm font-semibold text-text hover:bg-slate-50">Raise invoice issue</Link>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted" data-testid="no-invoice">No invoice has been issued for this appointment yet.</p>
            )}
          </Card>

          <Card title="Activity" id="history">
            {data.status_history?.length ? (
              <ol className="relative space-y-3 border-l border-border pl-4">
                {data.status_history.map((entry, index) => (
                  <li key={`${entry.created_at}-${index}`} className="text-sm">
                    <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full bg-primary" aria-hidden="true" />
                    <span className="font-semibold text-text">{entry.new_status_label}</span>
                    <span className="text-muted"> · {formatDateTimeInZone(entry.created_at, timeZone)}</span>
                    {entry.changed_by ? <span className="text-muted"> · by {entry.changed_by}</span> : null}
                    {entry.notes ? <p className="text-xs text-muted">{entry.notes}</p> : null}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-muted">No activity yet.</p>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          <Card title="Payment" id="payment">
            <p className={`text-sm font-semibold ${payment.reserved_payment_required ? "text-amber-800" : "text-text"}`} data-testid="payment-label">{payment.label}</p>
            {payment.reserved_payment_required && payment.payment_deadline ? (
              <p className="mt-1 text-xs text-muted">Pay by {formatDateTimeInZone(payment.payment_deadline, timeZone)} to keep this time.</p>
            ) : null}
          </Card>

          <Card title="Cancellation policy" id="policy">
            <div data-testid="detail-cancellation-policy">
              <p className="text-sm font-medium text-text">{policy.headline}</p>
              {policy.details.map((line) => (
                <p key={line} className="mt-1 text-xs text-muted">{line}</p>
              ))}
            </div>
            {actions.can_cancel && preview.patient_fee_would_apply ? (
              <p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-900">Cancelling now is outside the free-cancellation window; the clinic will review any fee.</p>
            ) : null}
            {!actions.can_cancel && reasons.cancel && !actions.is_terminal ? (
              <p className="mt-2 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid="cancel-unavailable">
                <span className="font-semibold">Cancellation unavailable.</span> {reasons.cancel.message}
              </p>
            ) : null}
          </Card>
        </div>
      </div>

      <CancelAppointmentDialog open={cancelOpen} appointment={data} onClose={() => setCancelOpen(false)} onCancelled={afterCancel} />
      {rescheduleOpen ? (
        <RescheduleDialog open appointment={data} displayTimezone={timeZone} onClose={() => setRescheduleOpen(false)} onRescheduled={afterReschedule} />
      ) : null}
    </div>
  );
}
